/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as Easing from 'eases';

import TWEEN from '@tweenjs/tween.js';
import isMobile from 'is-mobile-with-ipad';

import Background from '../gfx/Background';
import MinimalGrid from '../gfx/MinimalGrid';

import Scaleable from '../gfx/Scaleable';

import BaseScene from './BaseScene';

import Controller from '../hud/Controller';
import ARAnnotation from '../ar/ARAnnotation';

import DOMFadeable from '../html/DOMFadeable';

import * as Constants from '../constants/Constants';

// Fonts
import { font16, font24, font32, font48, font64 } from '../fonts/fonts';

import * as Annotations from '../data/Annotations';
import * as Textures from '../textures/Textures';

let RENDERORDER = {
  BACKGROUND: 1,
  GRID: 2,
  MODEL: 3,
  PILL: -1,
  LABEL: 1000,
  BUTTON: 6,
};

let MODEL = 'chacmool';

export default class ChacmoolScene extends BaseScene {
  constructor(props) {
    super(props);
    this.setupProperties(props);
  }

  setupProperties = props => {
    this.debug = props.debug != undefined ? props.debug : false;
    this.hitElements = new THREE.Scene();
    this.uiElements = new THREE.Scene();
    this.mobile = isMobile();
  };

  setup = props => {
    // this.renderer.domElement.style.zIndex = 10;
    this.renderer.context.getExtension('OES_standard_derivatives');
    this.setupHTMLHooks();
    this.setupCamera();
    this.setupScreenCamera();
    this.setupControls();
    this.loadModel();
    this.setupEvents();
  };

  setupHTMLHooks = () => {
    this.hideAnnotationsButtonElement = document.getElementById(
      'hide-ar-annotations-button'
    );
    this.hideAnnotationsButton = new DOMFadeable({
      element: this.hideAnnotationsButtonElement,
    });
    this.showAnnotationsButtonElement = document.getElementById(
      'show-ar-annotations-button'
    );
    this.showAnnotationsButton = new DOMFadeable({
      element: this.showAnnotationsButtonElement,
    });

    this.hideAnnotationsButtonElement.addEventListener('click', () => {
      this.tweenAnnotations(0.0001, Constants.duration, 0);
      this.showAnnotationsButton.fadeIn();
      this.hideAnnotationsButton.fadeOut();
    });

    this.showAnnotationsButtonElement.addEventListener('click', () => {
      this.tweenAnnotations(1.0, Constants.duration, 0);
      this.hideAnnotationsButton.fadeIn();
      this.showAnnotationsButton.fadeOut();
    });
  };

  setupEvents = () => {
    this.addEventListener('reset', () => {
      this.resetCamera();
    });
  };

  loadModel = () => {
    let worker;
    worker = new Worker('./js/load.worker.js');
    worker.postMessage({
      path: 'model/chacmool.drc',
    });
    worker.onmessage = e => {
      let geo = e.data;
      let geometry = new THREE.BufferGeometry();
      geometry.setIndex(new THREE.BufferAttribute(geo.index.array, 1));
      geometry.addAttribute(
        'position',
        new THREE.Float32BufferAttribute(geo.attributes.position.array, 3)
      );
      geometry.addAttribute(
        'normal',
        new THREE.Float32BufferAttribute(geo.attributes.normal.array, 3)
      );
      geometry.addAttribute(
        'uv',
        new THREE.Float32BufferAttribute(geo.attributes.uv.array, 2)
      );
      let mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
          map: Textures.Model,
          side: THREE.DoubleSide,
        })
      );
      this.setupModel(mesh);
      worker.terminate();
    };
  };

  setupModel = mesh => {
    this.model = new THREE.Object3D();
    mesh.renderOrder = RENDERORDER.MODEL;
    mesh.traverse(child => {
      if (child.type == 'Mesh') {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    this.bb = new THREE.Box3();
    this.bb.setFromObject(mesh);

    let center = new THREE.Vector3();
    this.bb.getCenter(center);

    let size = new THREE.Vector3();
    this.bb.getSize(size);

    mesh.position.set(-center.x, -this.bb.min.y, -center.z);
    mesh.updateMatrixWorld(true);
    this.model.add(mesh);
    this.add(this.model);

    this.setupAnnotations();
    this.setupAnnotationsTween();
    this.setupController();
    this.setupShadow();
    this.setupLights();
    this.setupModelTween();
    this.setupGrid();
    this.setupBackground();
    this.forceRender = true;
    this.loaded();
  };

  setupAnnotations = () => {
    const buttonSize = 64;
    let color = new THREE.Color(0x6bc4ff);

    let keys = Object.keys(Annotations);
    keys.forEach(key => {
      let data = Annotations[key];
      let name = data['label'];
      let type = data['type'];
      let action = data['action'];

      let normal = new THREE.Vector3();
      normal.fromArray(data['normal']);
      let position = new THREE.Vector3();
      position.fromArray(data['position']);

      //Create ARButton
      let button = new ARAnnotation({
        debug: false,
        camera: this.screenCamera,
        size: buttonSize,
        normal: normal,
        renderOrder: RENDERORDER.BUTTON,
        dot: {
          size: buttonSize,
          color: color,
          alpha: 0.75,
        },
        label: {
          color: new THREE.Color(0xffffff),
          alpha: 1.0,
          text: data['label'],
          font: font48,
          renderOrder: RENDERORDER.LABEL,
          padding: 8,
        },
        pill: {
          color: color,
          alpha: 0.75,
          renderOrder: RENDERORDER.PILL,
        },
      });

      button.fadeIn(100);
      button.name = name;
      button.position.copy(position);
      button.addEventListener('click', () => {
        if (action) {
          action();
        }
      });
      this.hitElements.add(button.getHitMesh());
      this.uiElements.add(button);
    });

    this.model.add(this.uiElements);
  };

  setupAnnotationsTween = () => {
    let scale = 0.0001;
    this.annotationsTweenValue = { value: scale };
    this.annotationsTween = new TWEEN.Tween(this.annotationsTweenValue);
    this.annotationsTween.easing(TWEEN.Easing.Cubic.InOut);

    this.annotationsTween.onUpdate(tween => {
      this.forceRender = true;
      let scale = this.annotationsTweenValue.value;
      let children = this.uiElements.children;
      children.forEach(child => {
        child.scale.set(scale, scale, scale);
      });
    });
    // this.tweenAnnotations(1.0);
  };

  setupController = () => {
    this.controller = new Controller({
      scene: this.hitElements,
      camera: this.screenCamera,
      canvas: this.canvas,
    });
  };

  setupShadow = () => {
    let size = new THREE.Vector3();
    this.bb.getSize(size);
    let geo = new THREE.PlaneBufferGeometry(
      size.x * 1.05,
      size.z * 1.175,
      2,
      2
    );
    let mat = new THREE.MeshBasicMaterial({
      map: Textures.Shadow,
      opacity: 0.125,
      transparent: true,
      depthWrite: false,
    });

    this.modelShadow = new THREE.Mesh(geo, mat);
    this.modelShadow.geometry.applyMatrix(
      new THREE.Matrix4().makeRotationX(THREE.Math.degToRad(-90))
    );
    this.modelShadow.renderOrder = RENDERORDER.SHADOW;
    this.model.add(this.modelShadow);
  };

  castShadow = (mesh, value) => {
    mesh.castShadow = value;
    mesh.receiveShadow = value;
    mesh.children.forEach(child => {
      this.castShadow(child, value);
    });
  };

  setupModelTween = () => {
    let scale = 0.0001;
    this.modelScale = { value: scale };
    this.model.scale.set(scale, scale, scale);
    this.model.updateMatrixWorld(true);
    this.tween = new TWEEN.Tween(this.modelScale);
    this.tween.easing(TWEEN.Easing.Cubic.InOut);

    this.tween.onUpdate(tween => {
      this.forceRender = true;
      let scale = this.modelScale.value;
      this.model.scale.set(scale, scale, scale);
    });
  };

  setupGrid = () => {
    this.minimalGrid = new MinimalGrid({
      gridColor: new THREE.Color(0xffffff),
      gridAlpha: 0.75,
      size: 5,
      interval: 0.25,
      thickness: 0.005,
    });
    this.minimalGrid.onUpdate(tween => {
      this.forceRender = true;
    });
    this.minimalGrid.renderOrder = RENDERORDER.GRID;
    this.minimalGrid.fadeOut(0);
  };

  setupBackground = () => {
    this.background = new Background({
      size: 4,
      skyColor: new THREE.Color(0xffffff),
      groundColor: new THREE.Color(0xcccccc),
      alpha: 1.0,
    });
    this.background.onUpdate(tween => {
      this.forceRender = true;
    });
    this.background.renderOrder = RENDERORDER.BACKGROUND;
    this.background.fadeOut(0);
  };

  setupCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.camera = new THREE.PerspectiveCamera(
      45,
      size.width / size.height,
      0.1,
      1000
    );
  };

  setupScreenCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.screenCamera = new THREE.OrthographicCamera(
      0,
      size.width,
      size.height,
      0,
      -1,
      1
    );
  };

  updateScreenCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    let width = size.width;
    let height = size.height;
    let aspect = width / height;
    this.screenCamera.right = width;
    this.screenCamera.top = height;
    this.screenCamera.aspect = aspect;
    this.screenCamera.updateProjectionMatrix();
  };

  setupControls = () => {
    this.controls = new THREE.OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    let speed = 0.5 / this.renderer.getPixelRatio();
    this.controls.zoomSpeed = speed;
    this.controls.rotateSpeed = speed;
    this.controls.panSpeed = speed;
    this.controls.minPolarAngle = -Math.PI / 8.0; // radians
    this.controls.maxPolarAngle = 1.6 * (Math.PI / 3.0); // radians
    this.controls.minDistance = 0.5;
    this.controls.maxDistance = 6;
    this.resetCamera();
  };

  updateCamera = () => {
    let size = this.renderer.getDrawingBufferSize();
    this.camera.aspect = size.width / size.height;
    this.camera.updateProjectionMatrix();
  };

  resetCamera = () => {
    this.camera.position.set(-1.25, 0.75, 1.25);
    this.controls.target.set(-0.05, 0.35, 0.0);
    this.camera.updateMatrixWorld(true);
  };

  setupLights = () => {
    let lightTempColor = 0xffffff;
    this.ambientIntensity = 2.0;
    this.ambient = new THREE.HemisphereLight(
      0xffffff,
      0xbbbbbb,
      this.ambientIntensity
    );
    this.ambient.position.set(0, 2.5, 0);
    this.add(this.ambient);

    let light = new THREE.DirectionalLight(0xffffff, 1.0);
    light.position.set(-10.0, 10.0, 10.0);
    light.lookAt(0.0, 0.0, 0.0);
    this.add(light);

    let light2 = new THREE.DirectionalLight(0xffffff, 1.0);
    light2.position.set(-10.0, 10.0, -10.0);
    light2.lookAt(0.0, 0.0, 0.0);
    this.add(light2);
  };

  update = time => {
    this.controls.update();
    this.time = time;
  };

  render = (camera, frame) => {
    if (this.forceRender || this.debug || camera) {
      if (!camera) {
        if (this.background) {
          this.renderer.render(this.background, this.camera);
        }
        if (this.minimalGrid) {
          this.renderer.render(this.minimalGrid, this.camera);
        }
      }

      // Render Model
      this.uiElements.visible = false;
      const cam = camera ? camera : this.camera;
      this.renderer.render(this, cam);

      // // Render Annotations
      this.renderer.clearDepth();
      this.uiElements.visible = true;
      this.renderer.render(this.uiElements, cam);

      this.forceRender = false;
    }
  };

  resize = () => {
    this.updateCamera();
    this.updateScreenCamera();
    this.forceRender = true;
  };

  tweenModel = (value, duration, delay, cb) => {
    this.modelScale.value = this.model.scale.x;
    this.tween
      .to({ value: value }, duration, delay)
      .onComplete(() => {
        this.tween.onComplete(() => {});
        if (cb != undefined) {
          // Used setTimeout here because tween.js has an issue with nested
          // tween onComplete and start calls
          setTimeout(cb, 0);
        }
      })
      .start();
  };

  tweenAnnotations = (value, duration, delay, cb) => {
    let annotation = this.uiElements.children[0];
    this.annotationsTweenValue.value = annotation.scale.x;
    this.annotationsTween
      .to({ value: value }, duration, delay)
      .onComplete(() => {
        this.annotationsTween.onComplete(() => {});
        if (cb != undefined) {
          // Used setTimeout here because tween.js has an issue with nested
          // tween onComplete and start calls
          setTimeout(cb, 0);
        }
      })
      .start();
  };

  enter = (duration, delay, cb) => {
    this.controls.enabled = true;
    this.model.position.set(0, 0, 0);
    this.model.updateMatrixWorld(true);
    this.background.fadeIn(duration, delay, () => {
      this.minimalGrid.fadeIn(duration, delay, () => {
        this.tweenModel(1.0, duration, delay, cb);
      });
    });
  };

  exit = (duration, delay, cb) => {
    this.controls.enabled = false;
    this.tweenModel(0.0001, duration, delay, () => {
      this.minimalGrid.fadeOut(duration, delay);
      this.background.fadeOut(duration, delay, cb);
    });
  };

  enterAR = (duration, delay, cb) => {
    this.modelShadow.material.opacity = 0.25;
    this.controls.enabled = false;
    this.tweenModel(1.0, duration, delay, () => {
      this.tweenAnnotations(1.0, duration, delay, cb);
      this.hideAnnotationsButton.fadeIn();
      this.showAnnotationsButton.fadeOut();
    });
  };

  exitAR = (duration, delay, cb) => {
    this.controls.enabled = true;
    this.hideAnnotationsButton.fadeOut();
    this.showAnnotationsButton.fadeOut();
    this.tweenAnnotations(0.0001, duration, delay, () => {
      this.tweenModel(0.0001, duration, delay, () => {
        this.modelShadow.material.opacity = 0.125;
        cb();
      });
    });
  };
}



// WEBPACK FOOTER //
// ./src/scenes/ChacmoolScene.js